import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Image,
  CloseButton,
  Badge,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { addProduct } from "../../app/features/cart/cartSlice";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import Noimage from "../../Images/Noimage.svg";

const getAuthToken = () => {
  const userData = sessionStorage.getItem("userData");
  if (!userData) return "";
  try {
    const parsed = JSON.parse(userData);
    return parsed.token || "";
  } catch {
    return "";
  }
};

if (typeof document !== "undefined") {
  const styleId = "slide-right-cart-style";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      .modal.fade .modal-dialog.slide-right {
        transform: translateX(100%);
        transition: transform 0.3s ease-out;
      }
      .modal.show .modal-dialog.slide-right {
        transform: translateX(0);
      }
      .modal.slide-right-modal .modal-dialog {
        position: fixed;
        right: 0;
        top: 0;
        margin: 0;
        height: 100%;
        max-width: 520px;
        width: 100%;
      }
      .modal.slide-right-modal .modal-content {
        height: 100%;
        border-radius: 0;
        border: none;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
      .hide-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      @media (max-width: 767px) {
        .modal.slide-right-modal .modal-dialog {
          width: 100% !important;
          max-width: 100% !important;
        }
      }
      .quick-view-qty-btn {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        font-weight: 600;
        font-size: 18px;
        color: #333;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        user-select: none;
      }
      .quick-view-qty-btn:hover {
        background-color: #e2e8f0;
        color: #7c0046;
      }
      input[type=number]::-webkit-inner-spin-button,
      input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type=number] {
        -moz-appearance: textfield;
      }
    `;
    document.head.appendChild(style);
  }
}

const ProductQuickViewModal = ({ show, onHide, product, productId }) => {
  const [quantity, setQuantity] = useState(1);
  const [productDetails, setProductDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [randomProducts, setRandomProducts] = useState([]);

  useEffect(() => {
    axios
      .get("https://vantara.datacubeglobal.com/api/random_products")
      .then((response) => {
        if (response.data.status === "success") {
          const mapped = response.data.data.map((p) => {
            let displayPrice = 0;
            let cutPrice = 0;

            if (p.offer && p.offer_price) {
              displayPrice = parseFloat(p.offer_price);
              if (p.offer.discount_from === "mrp") {
                cutPrice = parseFloat(p.mrp);
              } else if (p.offer.discount_from === "srp") {
                cutPrice = parseFloat(p.special_price);
              } else {
                cutPrice = parseFloat(p.mrp);
              }
            } else {
              displayPrice = parseFloat(p.special_price || p.mrp || 0);
              cutPrice = parseFloat(p.mrp || 0);
            }

            const discountPercent =
              cutPrice > displayPrice
                ? Math.round(((cutPrice - displayPrice) / cutPrice) * 100)
                : 0;

            return {
              id: p.product_id,
              title: p.product_name,
              price: displayPrice,
              originalPrice: cutPrice,
              discountPercent,
              img: p.primary_image
                ? `https://vantara.datacubeglobal.com/storage/${p.primary_image}`
                : Noimage,
            };
          });
          setRandomProducts(mapped);
        }
      })
      .catch((error) => console.error("Error fetching random products:", error));
  }, []);

  useEffect(() => {
    if (!productId || !show) return;

    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `https://vantara.datacubeglobal.com/api/api-products/${productId}`
        );

        let productData;
        if (Array.isArray(res.data)) {
          productData = res.data[0];
        } else if (res.data?.status === "success" && res.data.data) {
          productData = Array.isArray(res.data.data)
            ? res.data.data[0]
            : res.data.data;
        }

        if (productData) {
          setProductDetails(productData);
        }
      } catch (err) {
        console.error("Error fetching product details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, show]);

  const displayProduct = productDetails || product;

  const getMainImage = () => {
    if (productDetails?.primary_image) {
      return `https://vantara.datacubeglobal.com/storage/${productDetails.primary_image}`;
    }
    if (displayProduct?.image) return displayProduct.image;
    if (displayProduct?.img) return displayProduct.img;
    return Noimage;
  };

  const incrementQuantity = () => setQuantity((q) => q + 1);
  const decrementQuantity = () => setQuantity((q) => Math.max(1, q - 1));

  const handleAddToCart = async (targetProduct) => {
    const token = getAuthToken();
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const res = await axios.post(
        "https://vantara.datacubeglobal.com/api/cart",
        {
          product_id: targetProduct?.id || targetProduct?.product_id,
          quantity: quantity,
          options: "",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.status === "success") {
        dispatch(addProduct({ ...targetProduct, quantity }));

        Swal.fire({
          toast: true,
          icon: "success",
          title: "Added to Cart successfully",
          position: "top-end",
          showConfirmButton: false,
          timer: 1200,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          toast: true,
          icon: "info",
          title: res.data.message || "Unable to add to cart",
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      Swal.fire({
        toast: true,
        icon: "error",
        title: "Something went wrong!",
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className="slide-right-modal fade"
      dialogClassName="slide-right"
      backdrop={true}
      keyboard={true}
    >
      <Modal.Header className="border-bottom px-4 py-3 bg-white align-items-center">
        <Modal.Title className="h5 mb-0 fw-bold">Quick View</Modal.Title>
        <CloseButton onClick={onHide} />
      </Modal.Header>

      <Modal.Body className="p-0 overflow-auto bg-white d-flex flex-column">
        {loading ? (
          <div className="p-5 text-center text-muted">Loading product details...</div>
        ) : (
          <div className="p-4 d-flex flex-column gap-3">
            {/* Category Tag */}
            {displayProduct?.category && (
              <div>
                <span className="badge bg-light text-secondary text-uppercase fw-semibold px-2 py-1 rounded">
                  {displayProduct.category}
                </span>
              </div>
            )}

            {/* Product Image */}
            <div
              className="w-100 rounded-3 overflow-hidden d-flex align-items-center justify-content-center bg-light"
              style={{ minHeight: "220px", maxHeight: "300px" }}
            >
              <img
                src={getMainImage()}
                alt={displayProduct?.product_name || displayProduct?.title || "Product"}
                className="img-fluid"
                style={{ maxHeight: "280px", width: "100%", objectFit: "contain" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = Noimage;
                }}
              />
            </div>

            {/* Title */}
            <h4 className="fw-bold text-dark mb-1">
              {displayProduct?.product_name || displayProduct?.title}
            </h4>

            {/* Price Section */}
            <div className="d-flex align-items-center gap-2">
              {(() => {
                const mrp = parseFloat(displayProduct?.mrp || 0);
                const special = parseFloat(displayProduct?.special_price || 0);
                const offer = parseFloat(displayProduct?.offer_price || 0);

                let finalPrice = mrp;
                let strikePrice = null;

                if (offer > 0) {
                  finalPrice = offer;
                  strikePrice = displayProduct?.offer?.discount_from === "srp" ? (special > 0 ? special : mrp) : mrp;
                } else if (special > 0 && special < mrp) {
                  finalPrice = special;
                  strikePrice = mrp;
                } else {
                  finalPrice = mrp;
                }

                const discountPercent =
                  strikePrice && strikePrice > finalPrice
                    ? Math.round(((strikePrice - finalPrice) / strikePrice) * 100)
                    : 0;

                return (
                  <>
                    <span className="h4 fw-bold mb-0" style={{ color: "#7c0046" }}>
                      ₹{finalPrice.toFixed(2)}
                    </span>
                    {strikePrice && strikePrice > finalPrice && (
                      <span className="text-muted text-decoration-line-through small">
                        ₹{strikePrice.toFixed(2)}
                      </span>
                    )}
                    {discountPercent > 0 && (
                      <span className="badge bg-danger-subtle text-danger fw-bold ms-1">
                        {discountPercent}% OFF
                      </span>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Description */}
            {displayProduct?.description && (
              <p className="text-muted small mb-1" style={{ lineHeight: "1.5" }}>
                {displayProduct.description}
              </p>
            )}

            {/* Product Specifications Table */}
            <div className="bg-light p-3 rounded-3">
              <h6 className="fw-bold mb-2 small text-uppercase text-secondary">
                Product Details
              </h6>
              <div className="d-flex flex-column gap-1">
                {[
                  ["Category", displayProduct?.category],
                  ["Sub-Category", displayProduct?.sub_category],
                  ["Brand", displayProduct?.brand],
                  ["Size", displayProduct?.size],
                  ["Unit", displayProduct?.unit],
                  ["Weight", displayProduct?.weight],
                  ["Package Type", displayProduct?.package_type],
                  ["HSN Code", displayProduct?.hsn_code],
                ].map(([label, value], idx) =>
                  value && value !== "-" ? (
                    <div
                      key={idx}
                      className="d-flex justify-content-between py-1 border-bottom border-light-subtle small"
                    >
                      <span className="text-muted">{label}</span>
                      <span className="fw-medium text-dark">{value}</span>
                    </div>
                  ) : null
                )}
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="d-flex align-items-center gap-3 mt-1">
              <span className="fw-semibold small text-muted">Quantity:</span>
              <div
                className="d-flex align-items-center border rounded-pill bg-light overflow-hidden p-1 shadow-sm"
                style={{ height: "38px" }}
              >
                <button
                  type="button"
                  className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center p-0 border-0 shadow-sm"
                  style={{ width: "28px", height: "28px", backgroundColor: "#ffffff", color: "#333" }}
                  onClick={decrementQuantity}
                  aria-label="Decrease quantity"
                >
                  <i className="bi bi-dash fs-6 fw-bold"></i>
                </button>
                <input
                  type="number"
                  className="form-control text-center border-0 bg-transparent fw-bold text-dark p-0 shadow-none"
                  style={{ width: "36px", fontSize: "14px" }}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center p-0 border-0 shadow-sm"
                  style={{ width: "28px", height: "28px", backgroundColor: "#ffffff", color: "#333" }}
                  onClick={incrementQuantity}
                  aria-label="Increase quantity"
                >
                  <i className="bi bi-plus fs-6 fw-bold"></i>
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex flex-column gap-2 mt-2">
              {getAuthToken() ? (
                <>
                  <Button
                    style={{ backgroundColor: "#7c0046", borderColor: "#7c0046" }}
                    className="w-100 rounded-pill py-2 fw-semibold"
                    onClick={() => handleAddToCart(displayProduct)}
                  >
                    ADD TO CART &nbsp;₹
                    {(
                      (displayProduct?.offer_price
                        ? parseFloat(displayProduct.offer_price)
                        : (displayProduct?.special_price && parseFloat(displayProduct.special_price) > 0)
                          ? parseFloat(displayProduct.special_price)
                          : parseFloat(displayProduct?.mrp || 0)
                      ) * quantity
                    ).toFixed(2)}
                  </Button>

                  <Button
                    style={{ backgroundColor: "#124f14", borderColor: "#124f14" }}
                    className="w-100 rounded-pill py-2 fw-semibold"
                    onClick={async () => {
                      await handleAddToCart(displayProduct);
                      navigate("/checkout");
                    }}
                  >
                    BUY IT NOW
                  </Button>

                  <Button
                    variant="link"
                    size="sm"
                    onClick={onHide}
                    className="text-decoration-none text-muted small mt-1"
                  >
                    OR CONTINUE SHOPPING
                  </Button>
                </>
              ) : (
                <div className="text-center p-3 bg-light rounded-3">
                  <p className="text-muted small mb-2">Please log in to add products to your cart.</p>
                  <Link to="/login" className="btn btn-sm btn-outline-dark rounded-pill px-4">
                    Login
                  </Link>
                </div>
              )}
            </div>

            {/* Recommended Products Strip (You May Also Like) */}
            {randomProducts.length > 0 && (
              <div className="mt-4 pt-3 border-top">
                <h6 className="fw-bold mb-3 small text-uppercase text-secondary">
                  You May Also Like
                </h6>
                <div
                  className="d-flex gap-3 overflow-x-auto pb-2 hide-scrollbar"
                  style={{ WebkitOverflowScrolling: "touch" }}
                >
                  {randomProducts.map((p) => (
                    <div
                      key={p.id}
                      className="flex-shrink-0 text-center border rounded-3 p-2 bg-light"
                      style={{ width: "125px", cursor: "pointer" }}
                      onClick={() => {
                        onHide();
                        navigate(`/productDetails/${p.id}`);
                      }}
                    >
                      <img
                        src={p.img}
                        alt={p.title}
                        className="img-fluid rounded mb-2"
                        style={{ height: "90px", width: "100%", objectFit: "contain" }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = Noimage;
                        }}
                      />
                      <div
                        className="small fw-semibold text-truncate mb-1"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {p.title}
                      </div>
                      <div className="fw-bold small" style={{ color: "#7c0046" }}>
                        ₹{p.price}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ProductQuickViewModal;










